import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Wrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  width: 100%;
  height: 100%;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #444;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 500px;
`;

const formatNumber = (value) =>
  value?.toLocaleString("pl-PL").replace(/,/g, " ");

const ZakupyByCitiesHeatmap = ({ zakupyData, clientsData }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("00");

  const availableYears = useMemo(() => {
    const years = new Set();
    zakupyData?.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      if (!isNaN(year)) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [zakupyData]);

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    if (!selectedYear) return;

    const computeValue = (row) =>
      parseFloat(row.K_42 || 0) + parseFloat(row.K_43 || 0);

    const filtered = zakupyData.filter((row) => {
      const year = row.Rok?.toString();
      const month = row.Miesiac?.toString().padStart(2, "0");
      return (
        year === selectedYear.toString() &&
        (selectedMonth === "00" || month === selectedMonth)
      );
    });

    const dostawcyToCity = {};
    clientsData.forEach((c) => {
      if (c.nip?.trim()) {
        dostawcyToCity[c.nip.trim()] = c.miejscowosc || "Nieznane";
      }
    });

    const aggregated = filtered.reduce((acc, row) => {
      const value = computeValue(row);
      const nip = row.NrDostawcy?.trim();
      const city = dostawcyToCity[nip] || "Nieznane";

      if (!acc[city]) {
        acc[city] = { city, total: 0, transactions: 0 };
      }
      acc[city].total += value;
      acc[city].transactions += 1;
      return acc;
    }, {});

    const finalData = Object.values(aggregated)
      .filter((item) => item.city !== "Nieznane")
      .map((item) => ({
        ...item,
        avgValue: item.transactions > 0 ? item.total / item.transactions : 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 20); // Top 20 cities

    setFilteredData(finalData);
  }, [zakupyData, clientsData, selectedYear, selectedMonth]);

  if (!filteredData.length) {
    return <p style={{ textAlign: "center" }}>Ładowanie danych...</p>;
  }

  const chartData = {
    labels: filteredData.map((c) => c.city),
    datasets: [
      {
        label: "Całkowite Zakupy",
        data: filteredData.map((c) => c.total),
        backgroundColor: "#9848c7",
        yAxisID: "y",
        type: "bar",
      },
      {
        label: "Liczba Transakcji",
        data: filteredData.map((c) => c.transactions),
        backgroundColor: "#4caf50",
        yAxisID: "y1",
        type: "bar",
      },
      {
        label: "Średnia Wartość",
        data: filteredData.map((c) => c.avgValue),
        borderColor: "#ff9800",
        backgroundColor: "rgba(255, 152, 0, 0.3)",
        yAxisID: "y2",
        type: "line",
        tension: 0.3,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        backgroundColor: "#333",
        titleColor: "#fff",
        bodyColor: "#fff",
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}`
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Miasta" },
        ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
      },
      y: {
        title: { display: true, text: "Całkowite Zakupy" },
        ticks: { callback: (v) => formatNumber(v) },
      },
      y1: {
        position: "right",
        title: { display: true, text: "Liczba Transakcji" },
        grid: { drawOnChartArea: false },
        ticks: { callback: (v) => formatNumber(v) },
      },
      y2: {
        position: "right",
        offset: true,
        title: { display: true, text: "Średnia Wartość" },
        grid: { drawOnChartArea: false },
        ticks: { callback: (v) => formatNumber(v) },
      },
    },
  };

  return (
    <Wrapper>
      <TitleWrapper>
        Porównanie zakupów w miastach
        <InfoIconWrapper data-tooltip-id="zakupy-cities-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>
      <InfoTooltip
        id="zakupy-cities-tooltip"
        place="right"
        content={
          <>
            <strong>Zakupy wg miast</strong><br />
            Wykres pokazuje całkowitą wartość zakupów,<br />
            liczbę transakcji oraz średnią wartość zakupu.
          </>
        }
      />

      <FilterRow>
        <FilterLabel>Rok:</FilterLabel>
        <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(e.target.value)}>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>

        <FilterLabel>Miesiąc:</FilterLabel>
        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <option value="00">Cały rok</option>
          {[...Array(12).keys()].map((m) => {
            const val = String(m + 1).padStart(2, "0");
            const name = new Date(2000, m).toLocaleString("pl-PL", { month: "long" });
            return <option key={val} value={val}>{name}</option>;
          })}
        </Select>
      </FilterRow>

      <ChartContainer>
        <Bar data={chartData} options={chartOptions} />
      </ChartContainer>
    </Wrapper>
  );
};

export default ZakupyByCitiesHeatmap;
